/**
 * HL-12595: closed A/B tests. Nothing reads their localStorage buckets or Mixpanel super-properties
 * any more, but browsers bucketed while a test ran still carry them (Mixpanel keeps super-properties
 * in its cookie across visits). Closing a test = adding it here; MixPanelTracker.init retires them.
 */
type RetiredAbTest = {
  storageKeys: string[];
  superProperties: string[];
  // Mixpanel `country` values where a live test reuses these super-properties, so they stay.
  liveInCountries?: string[];
};

const RETIRED_AB_TESTS: Record<string, RetiredAbTest> = {
  // DE WL2 vs WL3 funnel and the V3 pages it pinned
  "HL-12122": {
    storageKeys: ["wl_funnel_cell", "wl_funnel_assigned", "wl_funnel_redirected", "abtest_wl3_catalog", "abtest_weight_loss_v3"],
    superProperties: ["test_cell", "wl_questionnaire_arm"],
  },
  // HL-12122's funnel_variant; the UK WL vs WL2 test (HL-12704) registers it again. Keyed on the
  // `country` super-property, not the URL: /payment_page_callback has no country in its path.
  "HL-12122 funnel_variant": {
    storageKeys: [],
    superProperties: ["funnel_variant"],
    liveInCountries: ["UK"],
  },
  // DE WL questionnaire format
  "HL-9768": {
    storageKeys: ["abtest_questionnaire_format", "abtest_wl_questionnaire"],
    superProperties: ["questionnaire_variant"],
  },
  // DE WL email step position (HL-11800) + design (HL-11986)
  "HL-11986": {
    storageKeys: ["abtest_wl_email_step", "abtest_wl_email_step_design"],
    superProperties: ["wl_email_step_variant", "design_version"],
  },
  // DE WL summary page edit
  "HL-12029": {
    storageKeys: ["abtest_summary_edit"],
    superProperties: ["summary_edit_variant"],
  },
  // DE WL campaign product first
  "HL-11657": {
    storageKeys: ["abtest_dynamic_treatment_order_by_campaign", "abtest_dynamic_treatment_order_by_campaign_promoted_slug"],
    superProperties: ["Dynamic Treatment Order by Campaign", "promoted_product_slug"],
  },
  // UK WL starter dose
  "HL-11593": {
    storageKeys: ["abtest_WL_MinDose_Test"],
    superProperties: ["WL_MinDose_Test"],
  },
};

type SuperPropertyStore = {
  get_property: (name: string) => unknown;
  unregister: (name: string) => void;
};

/**
 * Drops what the closed tests left in this browser. Only what is actually there is touched
 * (every unregister rewrites the Mixpanel cookie), so for most visitors this is a no-op.
 * `mixpanel` is null when Mixpanel is not configured.
 */
export const retireClosedAbTests = (mixpanel: SuperPropertyStore | null): void => {
  const tests = Object.values(RETIRED_AB_TESTS);

  try {
    tests
      .flatMap((test) => test.storageKeys)
      .forEach((key) => localStorage.getItem(key) !== null && localStorage.removeItem(key));
  } catch {
    // storage blocked: nothing was stored either
  }

  if (!mixpanel) return;
  const country = String(mixpanel.get_property("country") ?? "");
  tests
    .filter((test) => !test.liveInCountries?.includes(country))
    .flatMap((test) => test.superProperties)
    .forEach((name) => mixpanel.get_property(name) !== undefined && mixpanel.unregister(name));
};
